import React from 'react'
import PropTypes from 'prop-types'
import { Select } from 'antd'
import { identity, isNil, prop } from 'ramda'
import { ASC, DESC } from 'utils/constants'
import styled from '@emotion/styled'

import Field from '../Field'

/**
 * Helpful sort for SelectMenu options array
 * @param {'title'|'value'} key
 * @param {'ASC'|'DESC'} direction
 * @returns {array<{key: string, value: string, title: string}>}
 */
export const sortOptions =
  (key = 'title', direction = ASC) =>
  (a, b) => {
    if (direction === ASC) {
      if (a[key] < b[key]) return -1
      if (a[key] > b[key]) return 1
    }
    if (direction === DESC) {
      if (a[key] < b[key]) return 1
      if (a[key] > b[key]) return -1
    }
    return 0
  }

const SelectMenu = ({
  options,
  showSearch,
  optionFilterProp,
  optionValueProp,
  optionLabelProp,
  filterOption,
  defaultActiveFirstOption,
  mask,
  onSearch,
  placeholder,
  width,
  'data-testid': dataTestId,
  ...rest
}) => (
  <Field
    {...rest}
    render={({ value, ...renderProps }) => (
      <StyledSelect
        {...renderProps}
        {...(filterOption !== undefined ? { filterOption } : {})}
        {...(defaultActiveFirstOption !== undefined
          ? { defaultActiveFirstOption }
          : {})}
        value={value}
        placeholder={placeholder}
        showSearch={showSearch}
        optionFilterProp={optionFilterProp || 'children'}
        optionLabelProp={optionLabelProp}
        onSearch={onSearch}
        width={width}
        data-testid={dataTestId}
      >
        {options.map((opt) => {
          if (isNil(prop('groupName', opt))) {
            const { key, value: optionValue, title, disabled } = opt
            return (
              <Select.Option
                key={key}
                value={optionValue}
                title={title}
                disabled={disabled}
              >
                {mask(title, optionValue)}
              </Select.Option>
            )
          }
          const { key, value: optVal, groupName, children: optChildren } = opt
          return (
            <Select.OptGroup key={key || groupName} label={groupName}>
              {optChildren.map((child) => {
                const childKey = optionValueProp
                  ? child[optionValueProp]
                  : child.key
                const childValue = optionValueProp
                  ? child[optionValueProp]
                  : child.value
                const childTitle = optionLabelProp
                  ? child[optionLabelProp]
                  : child.title
                return (
                  <Select.Option
                    key={childKey}
                    title={childTitle}
                    value={childValue}
                    className={child.className}
                    disabled={child.disabled}
                  >
                    {mask(childTitle || childValue, optVal)}
                  </Select.Option>
                )
              })}
            </Select.OptGroup>
          )
        })}
      </StyledSelect>
    )}
  />
)

SelectMenu.defaultProps = {
  mask: identity,
  showSearch: false,
  placeholder: 'Select',
  onSearch: () => {},
}

/** see `Field` for more api info */
SelectMenu.propTypes = {
  /** boolean to filter based on keyboard input */
  showSearch: PropTypes.bool,
  /** mask to change display in some way */
  mask: PropTypes.func,
  /** placeholder text for unselected item */
  placeholder: PropTypes.string,
  /** props for nested/grouped options under their label when using OptGroup */
  optionLabelProp: PropTypes.string,
  optionValueProp: PropTypes.string,
  // filterOption and optionFilterProp can be used to create typeahead functionality
  // create match between typed input and option
  // this strategy is good when option title is a React node that may need extra calc
  optionFilterProp: PropTypes.string,
  filterOption: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  defaultActiveFirstOption: PropTypes.bool,
  /** options to render in menu */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.element,
      ]),
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.element,
      ]),
      disabled: PropTypes.bool,
      groupName: PropTypes.string,
      children: PropTypes.any,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSearch: PropTypes.func,
  width: PropTypes.number, // seems .ant-select-selection-selected-value always has to be manually set
  'data-testid': PropTypes.string,
}

export default SelectMenu

const StyledSelect = styled(Select)((props) => {
  const { width } = props

  return `
    .ant-select-selection-selected-value {
      width: ${width}px;
    }
  `
})
